/**
 * Genre Bootstrap card — appears CONTEXTUALLY when a signed-in user clicks
 * "↻ Shuffle" on Editor's Picks AND we detect they have no taste data yet
 * (empty bootstrap, empty watchlist). One-time, dismissible.
 *
 * Why: solves cold-start for personalization. A fresh user with no watchlist
 * would otherwise see generic picks for ~1 week until they save enough titles
 * to build a taste vector. This card lets them seed their taste in 1 tap.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkles, X } from "lucide-react";
import { toast } from "sonner";

// TMDB movie genre IDs (shared with backend ADJACENT_GENRES map).
// Picked to cover the most universal flavor profiles. Family (10751) is used
// as the "Feel Good" anchor since TMDB has no Feel-Good genre — the backend
// taste vector mixes Family/Comedy/Romance into the same warm cluster anyway.
const GENRE_CHIPS = [
  { id: 18,    label: "Drama",      emoji: "🎭" },
  { id: 53,    label: "Thriller",   emoji: "🔥" },
  { id: 35,    label: "Comedy",     emoji: "😂" },
  { id: 28,    label: "Action",     emoji: "💥" },
  { id: 10749, label: "Romance",    emoji: "💗" },
  { id: 9648,  label: "Mystery",    emoji: "🔍" },
  { id: 878,   label: "Sci-Fi",     emoji: "🚀" },
  { id: 27,    label: "Horror",     emoji: "👻" },
  { id: 10751, label: "Feel-Good",  emoji: "🌸" },
];

export default function GenreBootstrap({ onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Dismiss state persisted so it doesn't re-pop on next refresh
  useEffect(() => {
    try {
      if (localStorage.getItem("fm_bootstrap_done") === "1") setHidden(true);
    } catch (e) { /* private mode */ }
  }, []);

  const toggle = (id) => {
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 3 ? s : [...s, id])
    );
  };

  const onSave = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one vibe");
      return;
    }
    setSaving(true);
    try {
      await api.post("/me/bootstrap", { genre_ids: selected });
      try { localStorage.setItem("fm_bootstrap_done", "1"); } catch (e) {}
      toast.success("Your vibe is saved — refreshing picks");
      setHidden(true);
      onSaved?.();
    } catch (e) {
      toast.error("Couldn't save — try again?");
    } finally {
      setSaving(false);
    }
  };

  const onSkip = () => {
    try { localStorage.setItem("fm_bootstrap_done", "1"); } catch (e) {}
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <section className="px-6 lg:px-10 mt-3" data-testid="genre-bootstrap">
      <div className="relative max-w-[1440px] mx-auto rounded-xl border border-[#E50914]/30 bg-gradient-to-br from-[#E50914]/[0.08] via-black/40 to-[#007AFF]/[0.08] p-5 sm:p-6 overflow-hidden">
        <button
          onClick={onSkip}
          aria-label="Skip"
          data-testid="genre-bootstrap-skip"
          className="absolute top-3 right-3 p-1 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#E50914]/20 border border-[#E50914]/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#E50914]" />
          </div>
          <div>
            <h3 className="font-display text-base sm:text-lg font-bold tracking-tight text-white">
              Personalize your picks
            </h3>
            <p className="text-xs text-neutral-400 mt-0.5">
              Tap up to 3 vibes you love — we'll tune your home feed instantly. Don't worry, we still mix in universal must-watches.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {GENRE_CHIPS.map((g) => {
            const isOn = selected.includes(g.id);
            return (
              <button
                key={g.id}
                onClick={() => toggle(g.id)}
                data-testid={`genre-chip-${g.label.toLowerCase()}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all
                  ${isOn
                    ? "bg-[#E50914] border-[#E50914] text-white"
                    : "bg-white/5 border-white/15 text-neutral-300 hover:border-white/30 hover:bg-white/10"
                  }`}
              >
                <span className="mr-1.5">{g.emoji}</span>{g.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-neutral-500" data-testid="genre-bootstrap-count">
            {selected.length}/3 selected
          </p>
          <div className="flex gap-2">
            <button
              onClick={onSkip}
              data-testid="genre-bootstrap-skip-link"
              className="px-4 py-2 text-sm text-neutral-400 hover:text-white"
            >
              Skip
            </button>
            <button
              onClick={onSave}
              disabled={saving || selected.length === 0}
              data-testid="genre-bootstrap-save"
              className="px-5 py-2 rounded-full bg-white text-black text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-200 transition-colors"
            >
              {saving ? "Saving…" : "Save my vibe"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
